import React, { useRef } from 'react';
import { useLabels, usePrefs, useSitemap, useFlags } from 'wsm-common-data';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { useCardIntersectionObserver } from '../hooks/useCardObserver';
import RemoveButton from './common/RemoveButton';
import WebIntegrationPlaceholder from './WebIntegrationPlaceholder';

const VehicleCard = ({ vehicle, headerContent, showRemoveButton }) => {
	const { title = [], images = [], pricing = {}, uuid, link } = vehicle;
	const { compareCTA, compareCtaText } = usePrefs();
	const sitemap = useSitemap();

	const vehicleCardRef = useRef(null);
	const entry = useCardIntersectionObserver(vehicleCardRef);

	const isFullyVisible = (entry?.intersectionRatio ?? 1) >= 0.75;

	const { dprice = [] } = pricing;
	const finalPrice = dprice?.find((item) => item?.isFinalPrice);

	const labels = useLabels();

	const flags = useFlags();
	const isFixHeightImage = flags['srp-image-fix-height'];

	const {
		uri: imgUri = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
		title: imgTitle = '',
		alt = ''
	} = images?.[0] || {};

	return (
		<div
			{...{ inert: isFullyVisible ? undefined : 'true' }}
			ref={vehicleCardRef}
			className="ddc-font-size-small d-flex flex-column align-items-stretch h-100"
			data-uuid={uuid}
		>
			{/* Image */}
			<div>
				<div className="d-flex justify-content-end align-items-center text-muted mb-3">
					<span className="mr-2">{headerContent}</span>
					{showRemoveButton && <RemoveButton uuid={uuid} />}
				</div>

				<div
					className={setClassNames([
						'img-container',
						'text-center',
						'mb-3',
						...(isFixHeightImage
							? [
									'd-flex',
									'align-items-center',
									'overflow-hidden'
							  ]
							: [])
					])}
				>
					<img
						alt={alt}
						role="presentation"
						title={imgTitle}
						className="w-100 img-responsive"
						src={imgUri}
					/>
				</div>
			</div>

			{/* Title */}
			<div className="text-center">
				<p> {title.join(' ')}</p>
			</div>

			<div className="mt-auto">
				{/* Confirm Availability */}
				<button
					className="btn btn-primary btn-block mb-3 dialog"
					data-width="375"
					data-title={labels.get(compareCtaText)}
					target="dialog"
					data-href={`${sitemap.get(
						compareCTA
					)}?itemId=${uuid}&vehicleId=${uuid}`}
					type="button"
				>
					{labels.get(compareCtaText)}
				</button>
				{/* Price */}
				<div className="text-center font-weight-bold ddc-font-size-large">
					<p>{finalPrice?.value || labels.get('PLEASE_CALL')}</p>
				</div>
				<WebIntegrationPlaceholder location="vehicle-payments" />
				<WebIntegrationPlaceholder
					location="vehicle-payments-custom"
					data-vdp-link={link}
				/>
			</div>
		</div>
	);
};

VehicleCard.propTypes = {
	vehicle: PropTypes.shape({
		title: PropTypes.arrayOf(PropTypes.string),
		images: PropTypes.arrayOf(
			PropTypes.shape({
				uri: PropTypes.string,
				title: PropTypes.string,
				alt: PropTypes.string
			})
		),
		pricing: PropTypes.shape({
			dprice: PropTypes.arrayOf(
				PropTypes.shape({
					isFinalPrice: PropTypes.bool
				})
			)
		}),
		uuid: PropTypes.string.isRequired,
		link: PropTypes.string
	}),
	headerContent: PropTypes.string.isRequired,
	showRemoveButton: PropTypes.bool.isRequired
};

export default VehicleCard;
